/**
 * Porta do banco (ADR 0178): o contrato que `@/db/client` cumpre em QUALQUER runtime.
 *
 * No celular quem cumpre é o op-sqlite (`db/client.ts`). Nos testes, um SQLite do Node
 * (`test/dbHarness.ts`, `test/syncHarness.ts`). No Kontto Web completo, um SQLite em
 * WebAssembly no navegador. Repositórios, sync, backup e boot só conhecem ISTO — nunca o
 * driver. O formato de `SqlResult` é o que o código sempre consumiu do op-sqlite
 * (`rows` como objetos por nome de coluna + `rowsAffected`), então nada muda de semântica.
 */

/** Valor que pode ir num `?` do SQL (espelha o `Scalar` do op-sqlite). */
export type SqlParam = string | number | boolean | null | ArrayBuffer | ArrayBufferView;

export interface SqlResult {
    rows: Record<string, unknown>[];
    rowsAffected: number;
    insertId?: number;
}

export type SqlExecute = (sql: string, params?: SqlParam[]) => Promise<SqlResult>;

export interface RunInTransactionOptions {
    /**
     * 'off' desliga a checagem de FK durante ESTA transação (religada no fim). Use isto em vez
     * de executar `PRAGMA foreign_keys = OFF` ANTES de chamar: o SQLite ignora EM SILÊNCIO o
     * PRAGMA se houver transação aberta na conexão — e, com o mutex, outra transação pode estar
     * em voo exatamente nesse instante (o PRAGMA "não pegava" e o apply rodava com FK ligado).
     * Rodar o PRAGMA aqui, DEPOIS de adquirir o mutex e ANTES do BEGIN, garante que ele vale.
     */
    foreignKeys?: 'off';
}

export type RunInTransaction = <T>(fn: () => Promise<T>, opts?: RunInTransactionOptions) => Promise<T>;

/** O que o boot (`db/bootstrap.ts`) e o sync precisam de um runtime de banco. */
export interface DbRuntime {
    execute: SqlExecute;
    runInTransaction: RunInTransaction;
}

/**
 * Fabrica o `runInTransaction` de um runtime: executa `fn` numa transação ATÔMICA de verdade,
 * SERIALIZADA com as demais. Uma conexão por runtime, um mutex por conexão.
 *
 * ATENÇÃO: o `db.transaction()` do drizzle p/ op-sqlite NÃO faz rollback — ele
 * dispara begin/callback/commit sem `await` (o `run()` é assíncrono), então o
 * `try/catch` não pega a rejeição do callback, o `rollback` é código morto e o
 * `commit` acaba caindo depois da 1ª statement. Resultado: numa falha no meio, o
 * banco fica com escrita parcial (ex.: restore meio-apagado). Use SEMPRE este
 * helper em vez de `db.transaction()`.
 *
 * Como a conexão é única, qualquer `db.insert/update/delete/select` dentro de `fn`
 * participa da transação. Numa falha faz ROLLBACK e repropaga.
 *
 * MUTEX (fila FIFO): dois fluxos concorrentes (ex.: aplicador do sync no poll de
 * 45s × usuário salvando uma reserva) intercalavam na MESMA conexão — o BEGIN
 * aninhado do segundo falhava e o ROLLBACK do catch dele DERRUBAVA a transação
 * do primeiro, deixando o resto do primeiro fluxo em autocommit (perda parcial
 * silenciosa). Agora cada transação espera a anterior terminar. Por consequência,
 * ANINHAR runInTransaction (chamar dentro do callback de outro) passa de "erro
 * barulhento" a DEADLOCK — continua proibido; se um fluxo travar pra sempre,
 * procure aninhamento.
 */
export function createRunInTransaction(execute: SqlExecute): RunInTransaction {
    let txTail: Promise<unknown> = Promise.resolve();
    return async function runInTransaction<T>(fn: () => Promise<T>, opts?: RunInTransactionOptions): Promise<T> {
        const prev = txTail;
        let release!: () => void;
        txTail = new Promise<void>((resolve) => { release = resolve; });
        await prev;
        try {
            if (opts?.foreignKeys === 'off') await execute('PRAGMA foreign_keys = OFF');
            try {
                await execute('BEGIN');
                try {
                    const result = await fn();
                    await execute('COMMIT');
                    return result;
                } catch (err) {
                    await execute('ROLLBACK').catch(() => {});
                    throw err;
                }
            } finally {
                if (opts?.foreignKeys === 'off') {
                    await execute('PRAGMA foreign_keys = ON').catch(() => {});
                }
            }
        } finally {
            release();
        }
    };
}
